import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, ElementRef, HostListener, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ExtensionGroupOption } from 'src/app/pages/extension-register/models/extension-group-option.interface';
import {
   ExtensionChipOptions,
   ExtensionOption,
} from 'src/app/pages/extension-register/models/extension-option.interface';
import { LcsSingleSelectSelectedEvent } from '../../single-select/single-select-base';

import { MatChipInputEvent } from '@angular/material/chips';
import { ExtensionTypes } from 'generated/graphql-types';
import { ExtensionAndExtensionGroupSelectComponent } from '../component/extension-and-group-select.component';
import {
   ChipInputOption,
   ChipInputType,
   IncomingExtensionHuntGroupData,
   IncomingExtensionPagingGroupData,
   IncomingExtensionQueueData,
   IncomingExtensionSuperviseeData,
} from './extension-and-group.model';

@Component({
    providers: [{ multi: true, provide: NG_VALUE_ACCESSOR, useExisting: ExtensionAndExtensionGroupChipInputComponent }],
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-extension-and-group-chip-input',
    styleUrls: ['./extension-and-group-chip-input.component.scss'],
    templateUrl: './extension-and-group-chip-input.component.html',
    standalone: false
})
export class ExtensionAndExtensionGroupChipInputComponent implements ControlValueAccessor, OnInit {
   @Input() formDisabled: boolean = false;

   @Input() required: boolean = false;

   @Input() titleValue: string = 'Extensions';

   @Input() set extensionGroups(extensionGroups: ExtensionGroupOption[]) {
      if (extensionGroups !== undefined) {
         this._extensionGroups = extensionGroups.filter((extensionGroups) => {
            return !this.extensionGroupIdsToExclude.includes(extensionGroups.extensionGroupId);
         });
      }
   }

   @Input() set extensions(extensions: ExtensionChipOptions[]) {
      if (extensions !== undefined) {
         this._extensions = extensions.filter((extension) => {
            if (extension.hasOwnProperty('isArchived')) {
               return extension.type === ExtensionTypes.Phone && extension.isArchived === false;
            } else return extension.type === ExtensionTypes.Phone;
         });
      }
   }

   get extensions(): ExtensionChipOptions[] {
      return this._extensions;
   }

   get extensionGroups(): ExtensionGroupOption[] {
      return this._extensionGroups;
   }

   @Input() form: UntypedFormGroup;

   @Input() extensionIdsToExclude: number[] = [];

   @Input() extensionGroupIdsToExclude: number[] = [];

   @ViewChild('chipInput') chipInput: ElementRef<HTMLInputElement>;

   @ViewChild('groupSelect') groupSelect: ExtensionAndExtensionGroupSelectComponent;

   chipsControl = new UntypedFormControl();

   inputControl = new UntypedFormControl();

   values: ChipInputOption[] = [];

   availableGroups: ExtensionGroupOption[] = [];

   availableExtensions: ExtensionOption[] = [];

   filteredExtensionGroups: ExtensionGroupOption[] = [];

   filteredExtensions: ExtensionOption[] = [];

   searchTerm: string = '';

   noOptions: string = 'No Extensions Available';

   separatorKeysCodes: number[] = [ENTER, COMMA];

   private _extensions: ExtensionChipOptions[];

   private _extensionGroups: ExtensionGroupOption[];

   onChange = (values: ChipInputOption[]) => {};

   onTouched: () => void;

   get displayValues(): ChipInputOption[] {
      return this.values.filter((value) => !value.isDeleted);
   }

   ngOnInit() {}

   writeValue(
      value:
         | IncomingExtensionHuntGroupData
         | IncomingExtensionPagingGroupData
         | IncomingExtensionQueueData
         | IncomingExtensionSuperviseeData
   ): void {
      if (value) {
         let extensions: ChipInputOption[] = [];
         let extensionGroups: ChipInputOption[] = [];

         if ('huntGroupMembers' in value && 'huntGroupExtensionGroups' in value) {
            extensions = this.mapHuntGroupMembers(value.huntGroupMembers);
            extensionGroups = this.mapHuntGroupExtensionGroups(value.huntGroupExtensionGroups);
         } else if ('pagingGroupMembers' in value && 'pagingGroupExtensionGroups' in value) {
            extensions = this.mapPagingGroupMembers(value.pagingGroupMembers);
            extensionGroups = this.mapPagingGroupExtensionGroups(value.pagingGroupExtensionGroups);
         } else if ('queueMembers' in value && 'queueExtensionGroupMembers' in value) {
            extensions = this.mapQueueMembers(value.queueMembers);
            extensionGroups = this.mapQueueExtensionGroups(value.queueExtensionGroupMembers);
         } else if ('extensionSupervisees' in value && 'extensionGroupSupervisees' in value) {
            extensions = this.mapExtensionSupervisees(value.extensionSupervisees);
            extensionGroups = this.mapExtensionGroupSupervisees(value.extensionGroupSupervisees);
         }

         this.values = [...extensionGroups, ...extensions];
      } else {
         this.values = [];
      }

      this.chipsControl.setValue(value);
      this.removeErrors();
      this.updateExtensionOptions();
      this.updateExtensionGroupOptions();
   }

   registerOnChange(onChange: (values: ChipInputOption[]) => void) {
      this.onChange = onChange;
   }

   registerOnTouched(onTouched: () => void): void {
      this.onTouched = onTouched;
   }

   removeValue(value: ChipInputOption): void {
      const index = this.values.findIndex((v) => v.id === value.id && v.type === value.type);
      if (index !== -1) {
         if (this.values[index].id === 0) {
            this.values.splice(index, 1);
         } else {
            this.values[index].isDeleted = true;
         }
         this.updateValueAndControls();
      }
   }

   add(event: MatChipInputEvent): void {
      if (this.groupSelect.isOpen) {
         const value = event.value.trim();
         if (value) {
            const option = this.groupSelect.options.find((o) => o.value?.name?.toLowerCase() === value.toLowerCase());

            if (option?.value) {
               const chipType =
                  'extensionGroupId' in option.value ? ChipInputType.ExtensionGroup : ChipInputType.Extension;
               if (chipType === ChipInputType.ExtensionGroup) {
                  this.addChip(option.value, chipType);
               }
            }
         }
      }
   }

   selected(event: LcsSingleSelectSelectedEvent<ExtensionGroupOption | ExtensionOption>): void {
      if (event.option.value) {
         const type = 'extensionGroupId' in event.option.value ? ChipInputType.ExtensionGroup : ChipInputType.Extension;
         if (type == ChipInputType.Extension) this.addChip(event.option.value, type);
         if (type == ChipInputType.ExtensionGroup) this.addGroupChip(event.option.value, type);
      }
   }

   onInputChange(event: Event): void {
      this.searchTerm = (event.target as HTMLInputElement).value.trim().toLowerCase();
      this.filterOptions(this.searchTerm);
   }

   @HostListener('document:click', ['$event'])
   onClickOutside(event: MouseEvent) {
      this.searchTerm = '';
      this.filterOptions(this.searchTerm);
   }

   filterOptions(searchTerm: string): void {
      this.filteredExtensionGroups = this.availableGroups.filter(
         (group) =>
            group.name?.toLowerCase().includes(searchTerm) || group.description?.toLowerCase().includes(searchTerm)
      );
      this.filteredExtensions = this.availableExtensions.filter(
         (extension) =>
            (extension.number?.toString().toLowerCase().includes(searchTerm) ||
               extension.name?.toLowerCase().includes(searchTerm)) &&
            extension.type === ExtensionTypes.Phone
      );

      if (this.groupSelect) {
         this.groupSelect.updateFilteredOptions();
      }
   }

   private addChip(value: any, type: ChipInputType): void {
      const existingChipIndex = this.values.findIndex((chip) => chip.id === value.extensionId);

      if (existingChipIndex > -1) {
         this.values[existingChipIndex].isDeleted = false;
         this.updateValueAndControls();
         return;
      }

      const newValue: ChipInputOption = {
         id: value.extensionId,
         type: type,
         huntGroupExtensionGroupId: null,
         huntGroupMemberId: null,
         pagingGroupExtensionGroupId: null,
         pagingGroupMemberId: null,
         queueExtensionGroupId: null,
         queueMemberId: null,
         extensionGroupId: null,
         extensionId: value.extensionId,
         extensionGroupSuperviseeId: null,
         extensionSuperviseeId: null,
         value: value,
         isDeleted: false,
      };
      this.values = [...this.values, newValue];
      this.updateValueAndControls();
   }

   private addGroupChip(value: any, type: ChipInputType): void {
      const existingChipIndex = this.values.findIndex((chip) => chip.extensionGroupId === value.extensionGroupId);

      if (existingChipIndex > -1) {
         this.values[existingChipIndex].isDeleted = false;
         this.updateValueAndControls();
         return;
      }
      const newValue: ChipInputOption = {
         id: value.extensionGroupId,
         type: type,
         huntGroupExtensionGroupId: null,
         huntGroupMemberId: null,
         pagingGroupExtensionGroupId: null,
         pagingGroupMemberId: null,
         queueExtensionGroupId: null,
         queueMemberId: null,
         extensionGroupId: value.extensionGroupId,
         extensionId: null,
         extensionGroupSuperviseeId: null,
         extensionSuperviseeId: null,
         value: value,
         isDeleted: false,
      };
      this.values = [...this.values, newValue];
      this.updateValueAndControls();
   }

   private mapExtensionSupervisees(members: any[]): ChipInputOption[] {
      return members.map((item) => ({
         huntGroupExtensionGroupId: null,
         huntGroupMemberId: null,
         pagingGroupExtensionGroupId: null,
         pagingGroupMemberId: null,
         queueExtensionGroupId: null,
         queueMemberId: null,
         extensionGroupId: null,
         extensionId: item.extensionId,
         superviseeExtensionGroupId: null,
         extensionSuperviseeId: item.extensionSuperviseeId,
         extensionGroupSuperviseeId: null,
         id: item.extensionId || 0,
         type: ChipInputType.Extension,
         value: {
            type: 'Phone',
            ...item,
         } as ExtensionOption,
         isDeleted: false,
      }));
   }

   private mapExtensionGroupSupervisees(groups: any[]): ChipInputOption[] {
      return groups.map((item) => ({
         huntGroupExtensionGroupId: null,
         huntGroupMemberId: null,
         pagingGroupExtensionGroupId: null,
         pagingGroupMemberId: null,
         queueExtensionGroupId: null,
         queueMemberId: null,
         extensionId: null,
         extensionGroupSuperviseeId: item.extensionGroupSuperviseeId,
         extensionSuperviseeId: null,
         extensionGroupId: item.superviseeExtensionGroupId,
         id: item.superviseeExtensionGroupId || 0,
         type: ChipInputType.ExtensionGroup,
         value: item as ExtensionGroupOption,
         isDeleted: false,
      }));
   }

   private mapHuntGroupMembers(members: any[]): ChipInputOption[] {
      return members.map((item) => ({
         huntGroupExtensionGroupId: null,
         huntGroupMemberId: item.huntGroupMemberId,
         pagingGroupExtensionGroupId: null,
         pagingGroupMemberId: null,
         queueExtensionGroupId: null,
         queueMemberId: null,
         extensionGroupId: null,
         extensionId: item.extensionId,
         extensionGroupSuperviseeId: null,
         extensionSuperviseeId: null,
         id: item.extensionId || 0,
         type: ChipInputType.Extension,
         value: {
            type: 'Phone',
            ...item.extension,
         } as ExtensionOption,
         isDeleted: false,
      }));
   }

   private mapHuntGroupExtensionGroups(groups: any[]): ChipInputOption[] {
      return groups.map((item) => ({
         huntGroupExtensionGroupId: item.huntGroupExtensionGroupId,
         huntGroupMemberId: null,
         pagingGroupExtensionGroupId: null,
         pagingGroupMemberId: null,
         queueExtensionGroupId: null,
         queueMemberId: null,
         extensionId: null,
         extensionGroupSuperviseeId: null,
         extensionSuperviseeId: null,
         extensionGroupId: item.extensionGroupId,
         id: item.extensionGroupId || 0,
         type: ChipInputType.ExtensionGroup,
         value: item.extensionGroup as ExtensionGroupOption,
         isDeleted: false,
      }));
   }

   private mapPagingGroupMembers(members: any[]): ChipInputOption[] {
      return members.map((item) => ({
         huntGroupExtensionGroupId: null,
         huntGroupMemberId: null,
         pagingGroupExtensionGroupId: null,
         pagingGroupMemberId: item.pagingGroupMemberId,
         queueExtensionGroupId: null,
         queueMemberId: null,
         extensionGroupSuperviseeId: null,
         extensionSuperviseeId: null,
         extensionGroupId: null,
         extensionId: item.extensionId,
         id: item.extensionId || 0,
         type: ChipInputType.Extension,
         value: {
            type: 'Phone',
            ...item.extension,
         } as ExtensionOption,
         isDeleted: false,
      }));
   }

   private mapPagingGroupExtensionGroups(groups: any[]): ChipInputOption[] {
      return groups.map((item) => ({
         huntGroupExtensionGroupId: null,
         huntGroupMemberId: null,
         pagingGroupExtensionGroupId: item.pagingGroupExtensionGroupId,
         pagingGroupMemberId: null,
         queueExtensionGroupId: null,
         queueMemberId: null,
         extensionId: null,
         extensionGroupSuperviseeId: null,
         extensionSuperviseeId: null,
         extensionGroupId: item.extensionGroupId,
         id: item.extensionGroupId || 0,
         type: ChipInputType.ExtensionGroup,
         value: item.extensionGroup as ExtensionGroupOption,
         isDeleted: false,
      }));
   }

   private mapQueueMembers(members: any[]): ChipInputOption[] {
      return members.map((item) => ({
         huntGroupExtensionGroupId: null,
         huntGroupMemberId: null,
         pagingGroupExtensionGroupId: null,
         pagingGroupMemberId: null,
         queueExtensionGroupId: null,
         extensionGroupSuperviseeId: null,
         extensionSuperviseeId: null,
         queueMemberId: item.extensionQueueMemberId,
         extensionGroupId: null,
         extensionId: item.extensionId,
         id: item.extensionId || 0,
         type: ChipInputType.Extension,
         value: {
            type: 'Phone',
            ...item,
         } as ExtensionOption,
         isDeleted: false,
      }));
   }

   private mapQueueExtensionGroups(groups: any[]): ChipInputOption[] {
      return groups.map((item) => ({
         huntGroupExtensionGroupId: null,
         huntGroupMemberId: null,
         pagingGroupExtensionGroupId: null,
         pagingGroupMemberId: null,
         queueExtensionGroupId: item.queueExtensionGroupId,
         queueMemberId: null,
         extensionGroupSuperviseeId: null,
         extensionSuperviseeId: null,
         extensionId: null,
         extensionGroupId: item.extensionGroupId,
         id: item.extensionGroupId || 0,
         type: ChipInputType.ExtensionGroup,
         value: item.extensionGroup as ExtensionGroupOption,
         isDeleted: false,
      }));
   }

   removeErrors(): void {
      if (!this.chipsControl.errors?.required) {
         this.chipsControl.setErrors(null);
      }
   }

   updateExtensionGroupOptions() {
      const selectedGroupIds = new Set(
         this.displayValues.filter((v) => v.type === ChipInputType.ExtensionGroup).map((v) => v.extensionGroupId)
      );

      this.availableGroups = this.extensionGroups.filter(
         (group) =>
            !selectedGroupIds.has(group.extensionGroupId) &&
            !this.extensionGroupIdsToExclude.includes(group.extensionGroupId)
      );

      this.filteredExtensionGroups = this.availableGroups;
   }

   updateExtensionOptions() {
      const selectedExtensionIds = new Set(
         this.displayValues.filter((v) => v.type === ChipInputType.Extension).map((v) => v.extensionId)
      );

      this.availableExtensions = this.extensions.filter(
         (extension) =>
            !selectedExtensionIds.has(extension.extensionId) &&
            !this.extensionIdsToExclude.includes(extension.extensionId) &&
            extension.type === ExtensionTypes.Phone
      );

      this.filteredExtensions = this.availableExtensions;
   }

   updateValueAndControls(): void {
      if (this.groupSelect.panel) {
         this.groupSelect.panel.visible = false;
      }

      this.chipInput.nativeElement.value = '';

      this.inputControl.setValue(null);

      this.chipsControl.setValue(this.displayValues);

      this.onChange(this.values);

      this.removeErrors();

      this.updateExtensionOptions();

      this.updateExtensionGroupOptions();
   }

   getChipDisplay(value: ChipInputOption): string {
      if (value.type === ChipInputType.ExtensionGroup) {
         const group = value.value as ExtensionGroupOption;

         if (!group.name || group.name.trim() === '') {
            return group.description ? `${group.description} (group)` : '';
         } else if (group.description && group.description.trim() !== '') {
            return `${group.name} - ${group.description} (group)`;
         } else {
            return `${group.name} (group)`;
         }
      } else {
         const extension = value.value as ExtensionOption;
         return `${extension.number} - ${extension.name}`;
      }
   }
}
